import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Listen to the native 'toggle' event — this gives us a single source of truth
    // for details.open changes (user click on <summary>, programmatic open/close, etc).
    this.refs.details.addEventListener('toggle', this.#onDetailsToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#onDetailsToggle);
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.close(); // close main drawer on Escape
  };

  #onDetailsToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (details.open) {
      this.#performOpen(details);
    } else {
      this.#performClose(details);
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Public open — make the details open. The native 'toggle' event will call performOpen.
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    if (!details) return;

    // If already open, ensure visual state
    if (!details.hasAttribute('open')) {
      // Use property to trigger native toggle (will emit 'toggle')
      details.open = true;
    } else {
      // fallback: if open attribute already present but we missed visual state
      this.#performOpen(details);
    }
  }

  /**
   * Go back or close the closest menu drawer
   * @param {Event} [event]
   */
  back(event) {
    // behaviour retained — close closest details (submenus or main)
    const details = this.#getDetailsElement(event);
    if (details && details !== this.refs.details) {
      details.open = false;
    } else {
      this.close();
    }
  }

  /**
   * Public close — remove open attribute. The native 'toggle' event will call performClose.
   */
  close() {
    const details = this.refs.details;
    if (!details) return;

    if (details.hasAttribute('open')) {
      details.open = false; // trigger native toggle -> #onDetailsToggle -> #performClose
    } else {
      // already closed — ensure visual clean state
      this.#performClose(details);
    }
  }

  /**
   * Performs the visual/behavioral "open" (called from #onDetailsToggle)
   * @param {HTMLDetailsElement} details
   */
  #performOpen(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.classList.add('menu-open');

    // Ensure correct icons
    this.#updateIconStates(details, true);

    // trap focus after paint
    requestAnimationFrame(() => {
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Performs the visual/behavioral "close" (called from #onDetailsToggle)
   * @param {HTMLDetailsElement} details
   */
  #performClose(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Ensure icon state right-away (prevents stale icon while animating)
    this.#updateIconStates(details, false);

    onAnimationEnd(details, () => {
      // Reset visual state and focus-trap as before
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Update the summary icons immediately (openState=true => show close icon)
   * @param {HTMLDetailsElement} details
   * @param {boolean} openState
   */
  #updateIconStates(details, openState) {
    const openIcon = details.querySelector('.header-drawer-icon--open');
    const closeIcon = details.querySelector('.header-drawer-icon--close');

    if (openIcon) openIcon.style.display = openState ? 'none' : 'flex';
    if (closeIcon) closeIcon.style.display = openState ? 'flex' : 'none';
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   */
  #setupAnimatedElementListeners() {
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
